import { JSONArray, JSONObject, JSONValue } from "@segment/analytics-next";
import {
  createRequestId,
  isOk,
  normalizeHeaders,
  normalizeRequestInfo,
  tryJSONParse,
} from "./helpers";

let origFetch: typeof window.fetch;
let origXHROpen: typeof XMLHttpRequest.prototype.open;
let origXHRSend: typeof XMLHttpRequest.prototype.send;
let origXHRSetRequestHeader: typeof XMLHttpRequest.prototype.setRequestHeader;

export type HTTPMethod =
  | "GET"
  | "POST"
  | "PUT"
  | "DELETE"
  | "PATCH"
  | "HEAD"
  | "OPTIONS";

const toHTTPMethod = (method: string): HTTPMethod => {
  return method.toUpperCase() as HTTPMethod;
};

export interface NetworkInterceptorRequest {
  url: string;
  method: HTTPMethod;
  contentType: string | undefined;
  body: string | undefined;
  headers: Headers;
  id: string;
}

export interface NetworkInterceptorResponse {
  url: string;
  status: number;
  ok: boolean;
  statusText: string;
  headers: Headers;
  body: () => Promise<JSONValue>;
  initiator: "fetch" | "xhr";
  responseType: XMLHttpRequestResponseType | undefined;
  req: {
    id: string;
  };
}

const createInterceptorRequest = ({
  url,
  body,
  headers,
  id,
  method,
}: {
  url: URL | string;
  body: string | undefined;
  method: HTTPMethod;
  headers: Headers | undefined;
  id: string;
}): NetworkInterceptorRequest => ({
  url: url.toString(),
  method: method,
  headers: headers ?? new Headers(),
  contentType: headers?.get("content-type") ?? undefined,
  body: typeof body == "string" ? body : undefined,
  id,
});

const createInterceptorResponse = ({
  body,
  headers,
  initiator,
  status,
  statusText,
  url,
  responseType,
  id,
}: {
  body: () => Promise<JSONValue>;
  headers: Headers;
  initiator: "fetch" | "xhr";
  status: number;
  statusText: string;
  url: string;
  responseType: XMLHttpRequestResponseType | undefined;
  id: string;
}): NetworkInterceptorResponse => ({
  body,
  headers,
  initiator,
  ok: isOk(status),
  status,
  statusText,
  url,
  responseType,
  req: {
    id: id,
  },
});

/**
 * Taken from https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/response
 */

export interface NetworkResponseHandler {
  (rs: NetworkInterceptorResponse): void;
}

export interface NetworkRequestHandler {
  (rq: NetworkInterceptorRequest): void;
}

/**
 * NetworkInterceptor class to intercept network requests and responses
 */
export class NetworkInterceptor {
  private _isPatched = false;

  addInterceptors(
    onRequest: NetworkRequestHandler,
    onResponse: NetworkResponseHandler,
  ) {
    if (this._isPatched) {
      return;
    }
    this._isPatched = true;
    this.addFetchInterceptor(onRequest, onResponse);
    this.addXhrInterceptor(onRequest, onResponse);
  }
  private addFetchInterceptor(
    onRequest: NetworkRequestHandler,
    onResponse: NetworkResponseHandler,
  ) {
    if (!window.fetch) {
      return;
    }
    if ((window.fetch as any)?._segmentIntercepted) {
      return;
    }
    origFetch = window.fetch;
    const interceptedFetch = async (
      ...args: Parameters<typeof window.fetch>
    ) => {
      const [url, options] = args;
      const id = createRequestId();
      try {
        const normalizedURL = normalizeRequestInfo(url);
        const headers = options?.headers
          ? normalizeHeaders(options.headers)
          : undefined;

        onRequest(
          createInterceptorRequest({
            url: normalizedURL,
            body: typeof options?.body == "string" ? options.body : undefined,
            method: options?.method ? toHTTPMethod(options.method) : "GET",
            headers,
            id,
          }),
        );
      } catch (err) {
        console.log("Error handling request: ", err);
      }
      const ogResponse = await origFetch(...args);

      // response.text() etc is single use only -- to prevent conflicts with app code, use clone
      // https://developer.mozilla.org/en-US/docs/Web/API/Response/clone
      const response = ogResponse.clone();

      try {
        const lazyBody = async (): Promise<JSONValue> => {
          let text: string;
          try {
            text = await response.text();
          } catch (err) {
            console.warn("Error converting to text", err, response);
            return null;
          }
          return tryJSONParse(text); // should never throw.
        };

        onResponse(
          createInterceptorResponse({
            body: lazyBody,
            headers: response.headers,
            initiator: "fetch",
            status: response.status,
            statusText: response.statusText,
            url: response.url,
            responseType: undefined,
            id: id,
          }),
        );
      } catch (err) {
        console.log("Error handling response: ", err);
      }
      return ogResponse;
    };

    try {
      (interceptedFetch as any)._segmentIntercepted = true;
      window.fetch = interceptedFetch;
    } catch (err) {
      console.warn("Failed to patch fetch:", err);
    }
  }

  private addXhrInterceptor(
    onRequest: NetworkRequestHandler,
    onResponse: NetworkResponseHandler,
  ) {
    if (!window.XMLHttpRequest) {
      return;
    }
    if ((XMLHttpRequest.prototype as any)?._segmentIntercepted) {
      return;
    }

    // Store original methods
    origXHROpen = XMLHttpRequest.prototype.open;
    origXHRSend = XMLHttpRequest.prototype.send;
    origXHRSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

    // Helper to parse XHR headers
    const getParsedXHRHeaders = (allResponseHeaders: string): Headers => {
      const headers = new Headers();
      allResponseHeaders
        .trim()
        .split(/[\r\n]+/)
        .forEach((line) => {
          const parts = line.split(": ");
          const header = parts.shift();
          const value = parts.join(": ");
          if (header) {
            headers.append(header, value);
          }
        });
      return headers;
    };

    // Helper to parse XHR body
    const getParsedXHRBody = (xhr: XMLHttpRequest): JSONValue => {
      if (xhr.responseType === "json") {
        return xhr.response as JSONObject | JSONArray | null;
      } else if (
        xhr.responseType === "text" ||
        xhr.responseType === "" ||
        typeof xhr.response === "string"
      ) {
        return tryJSONParse(xhr.response);
      } else if (
        xhr.responseType === "blob" ||
        xhr.responseType === "arraybuffer"
      ) {
        // Return metadata for binary types instead of null
        return {
          type: xhr.responseType,
          size: xhr.response?.size ?? xhr.response?.byteLength ?? 0,
        };
      }
      return null;
    };

    // Patch open method
    XMLHttpRequest.prototype.open = function (
      this: XMLHttpRequest & {
        _reqURL?: string;
        _reqMethod?: HTTPMethod;
        _reqBody?: XMLHttpRequestBodyInit;
        _reqHeaders?: Headers;
        _reqId?: string;
        _listenerAdded?: boolean;
      },
      ...args:
        | [method: string, url: string | URL]
        | [
            method: string,
            url: string | URL,
            async: boolean,
            username?: string | null,
            password?: string | null,
          ]
    ) {
      const [method, url] = args;
      try {
        this._reqURL = url.toString();
        this._reqMethod = toHTTPMethod(method);
        // Only generate request ID once per XHR instance
        if (!this._reqId) {
          this._reqId = createRequestId();
        }

        // Add readystatechange listener once per XHR instance
        if (!this._listenerAdded) {
          this._listenerAdded = true;

          this.addEventListener("readystatechange", () => {
            // Handle request
            if (this.readyState === this.HEADERS_RECEIVED) {
              try {
                onRequest(
                  createInterceptorRequest({
                    url: this._reqURL!,
                    method: this._reqMethod!,
                    headers: this._reqHeaders,
                    id: this._reqId!,
                    body: this._reqBody ? this._reqBody.toString() : undefined,
                  }),
                );
              } catch (err) {
                console.log("Error handling request", err);
              }
            }
            // Handle response
            if (this.readyState === this.DONE) {
              try {
                onResponse(
                  createInterceptorResponse({
                    status: this.status,
                    responseType: this.responseType,
                    statusText: this.statusText,
                    url: this.responseURL,
                    initiator: "xhr",
                    body: () => Promise.resolve(getParsedXHRBody(this)),
                    headers: getParsedXHRHeaders(this.getAllResponseHeaders()),
                    id: this._reqId!,
                  }),
                );
              } catch (err) {
                console.log("Error handling response", err);
              }
            }
          });
        }
      } catch (err) {
        console.log("Error handling request (open)", err);
      }

      return (origXHROpen as (...args: any[]) => void).apply(this, args);
    };

    // Patch send method
    XMLHttpRequest.prototype.send = function (
      this: XMLHttpRequest & {
        _reqBody?: XMLHttpRequestBodyInit;
      },
      body?: XMLHttpRequestBodyInit,
    ) {
      this._reqBody = body;
      return origXHRSend.call(this, body);
    };

    // Patch setRequestHeader method
    XMLHttpRequest.prototype.setRequestHeader = function (
      this: XMLHttpRequest & {
        _reqHeaders?: Headers;
      },
      ...args: Parameters<typeof XMLHttpRequest.prototype.setRequestHeader>
    ) {
      try {
        const [header, value] = args;
        if (!this._reqHeaders) {
          this._reqHeaders = new Headers();
        }
        this._reqHeaders.append(header, value);
      } catch (err) {
        console.log("Error handling request (setRequestHeader)", err);
      }
      return origXHRSetRequestHeader.apply(this, args);
    };

    try {
      (XMLHttpRequest.prototype as any)._segmentIntercepted = true;
    } catch (err) {
      console.warn("Failed to mark XHR as intercepted:", err);
    }
  }

  cleanup() {
    if (!this._isPatched) {
      return;
    }

    // Restore fetch
    if (origFetch) {
      window.fetch = origFetch;
      try {
        delete (window.fetch as any)._segmentIntercepted;
      } catch (err) {
        // Ignore if property is non-configurable
      }
    }

    // Restore XHR prototype methods
    if (origXHROpen) {
      XMLHttpRequest.prototype.open = origXHROpen;
    }
    if (origXHRSend) {
      XMLHttpRequest.prototype.send = origXHRSend;
    }
    if (origXHRSetRequestHeader) {
      XMLHttpRequest.prototype.setRequestHeader = origXHRSetRequestHeader;
    }
    try {
      delete (XMLHttpRequest.prototype as any)._segmentIntercepted;
    } catch (err) {
      // Ignore if property is non-configurable
    }

    this._isPatched = false;
  }
}
